import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { JobStatusBadge } from '@/components/JobStatusBadge';
import { useJob } from '@/hooks/useJobs';
import { useDeleteJob } from '@/hooks/useDeleteJob';
import { useCloneJob } from '@/hooks/useCloneJob';
import { useAuth } from '@/contexts/AuthContext';
import { Copy, Loader2 } from 'lucide-react';

// Format ISO date to DD/MM/YYYY without timezone offset issues
function formatDateBR(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return new Date(isoDate).toLocaleDateString('pt-BR');
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
import { JobStatus, APPROVAL_THRESHOLD } from '@recruitai/shared';
import { AppLayout } from '@/components/AppLayout';
import { LinkedInPublishCard } from '@/components/LinkedInPublishCard';
import { apiClient } from '@/lib/api-client';

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { job, isLoading, error, refetch } = useJob(jobId!);
  const { deleteJob, isLoading: deleting } = useDeleteJob();
  const { cloneJob, isCloning } = useCloneJob();
  const { user } = useAuth();
  const canClone = user?.role === 'recruiter' || user?.role === 'admin' || user?.role === 'owner';
  const [changingStatus, setChangingStatus] = useState(false);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Initialize threshold from job data
  const currentThreshold = threshold ?? job?.approvalThreshold ?? APPROVAL_THRESHOLD;

  const handleSaveThreshold = async () => {
    try {
      setSavingThreshold(true);
      const result = await apiClient.patch<{
        approvalThreshold: number;
        oldThreshold: number;
        reclassifying: boolean;
      }>(`/jobs/${jobId}/threshold`, { approvalThreshold: currentThreshold });

      if (result.reclassifying) {
        toast.success('Threshold atualizado! Os candidatos estão sendo reclassificados em segundo plano. Atualize a página em alguns instantes.');
      } else {
        toast.success('Threshold atualizado!');
      }
      await refetch();
    } catch (error) {
      console.error('Failed to update threshold:', error);
      toast.error('Erro ao atualizar threshold. Tente novamente.');
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja deletar esta vaga?')) {
      return;
    }

    try {
      await deleteJob(jobId!);
      toast.success('Vaga deletada com sucesso!');
      navigate('/jobs');
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Erro ao deletar vaga. Tente novamente.');
    }
  };

  const handleStatusChange = async (newStatus: JobStatus) => {
    try {
      setChangingStatus(true);
      const { apiClient } = await import('../lib/api-client');
      await apiClient.patch(`/jobs/${jobId}/status`, { status: newStatus });
      toast.success('Status atualizado com sucesso!');
      await refetch();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Erro ao atualizar status. Tente novamente.');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleClone = async () => {
    try {
      const cloned = await cloneJob(jobId!);
      toast.success('Vaga clonada com sucesso');
      navigate(`/jobs/${cloned.jobId}/edit`);
    } catch (err: any) {
      if (err.code === 'QUOTA_EXCEEDED') {
        toast.warning('Limite de vagas do plano atingido. Faça upgrade para continuar.');
      } else if (err.code === 'FORBIDDEN') {
        toast.error('Você não tem permissão para clonar vagas.');
      } else {
        toast.error('Erro ao clonar vaga. Tente novamente.');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive mb-4">Vaga não encontrada</p>
            <Button onClick={() => navigate('/jobs')}>Voltar para Vagas</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AppLayout
      actions={
        <Button variant="outline" onClick={() => navigate('/jobs')}>
          Voltar
        </Button>
      }
    >
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-3xl font-bold">{job.title}</h2>
            <JobStatusBadge status={job.status} />
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
            <span>{job.candidateCount} candidatos</span>
            <span>•</span>
            <span>Criada em {new Date(job.createdAt).toLocaleDateString('pt-BR')}</span>
            {job.openedAt && (
              <>
                <span>•</span>
                <span>Aberta em {formatDateBR(job.openedAt)}</span>
              </>
            )}
            {job.expiresAt && (
              <>
                <span>•</span>
                <span>Expira em {formatDateBR(job.expiresAt)}</span>
              </>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {job.status === JobStatus.DRAFT && (
              <Button size="sm" onClick={() => handleStatusChange(JobStatus.OPEN)} disabled={changingStatus}>
                Publicar
              </Button>
            )}
            {job.status === JobStatus.OPEN && (
              <Button size="sm" variant="outline" onClick={() => handleStatusChange(JobStatus.CLOSED)} disabled={changingStatus}>
                Fechar
              </Button>
            )}
            {job.status === JobStatus.CLOSED && (
              <Button size="sm" variant="outline" onClick={() => handleStatusChange(JobStatus.OPEN)} disabled={changingStatus}>
                Reabrir
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${job.jobId}/edit`)}>
              Editar
            </Button>
            <Button size="sm" onClick={() => navigate(`/jobs/${job.jobId}/candidates`)}>
              Candidatos
            </Button>
            {canClone && (
              <Button size="sm" variant="outline" onClick={handleClone} disabled={isCloning}>
                {isCloning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                Clonar Vaga
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deletando...' : 'Deletar'}
            </Button>
          </div>
        </div>

        {/* LinkedIn */}
        <LinkedInPublishCard
          jobId={job.jobId}
          jobStatus={job.status}
        />

        {/* Threshold de Aprovação */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Score Mínimo de Aprovação</CardTitle>
            <CardDescription>
              Candidatos com score abaixo deste valor serão rejeitados automaticamente.
              Alterar o threshold reclassifica candidatos já analisados sem reprocessar pela IA.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs">
                <Label htmlFor="threshold">Threshold (0-100)</Label>
                <Input
                  id="threshold"
                  type="number"
                  min={0}
                  max={100}
                  value={currentThreshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                />
              </div>
              <Button
                onClick={handleSaveThreshold}
                disabled={savingThreshold || currentThreshold === (job.approvalThreshold ?? APPROVAL_THRESHOLD)}
              >
                {savingThreshold ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Descrição</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{job.description}</p>
          </CardContent>
        </Card>

        {/* Dates */}
        {(job.openedAt || job.expiresAt) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Datas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {job.openedAt && (
                  <div>
                    <p className="text-sm font-medium mb-1">Data de Abertura</p>
                    <p className="text-muted-foreground">
                      {formatDateBR(job.openedAt)}
                    </p>
                  </div>
                )}
                {job.expiresAt && (
                  <div>
                    <p className="text-sm font-medium mb-1">Data de Validade</p>
                    <p className="text-muted-foreground">
                      {formatDateBR(job.expiresAt)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Requirements */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Requisitos Obrigatórios</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2">
              {job.requirements.map((req, index) => (
                <li key={index} className="text-muted-foreground">{req}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Nice to Have */}
        {job.niceToHave && job.niceToHave.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Requisitos Desejáveis</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2">
                {job.niceToHave.map((req, index) => (
                  <li key={index} className="text-muted-foreground">{req}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Evaluation Criteria */}
        <Card>
          <CardHeader>
            <CardTitle>Critérios de Avaliação</CardTitle>
            <CardDescription>
              Como os candidatos serão avaliados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {typeof job.evaluationCriteria === 'object' && !Array.isArray(job.evaluationCriteria) ? (
                <>
                  <div className="flex justify-between">
                    <span>Experiência Técnica:</span>
                    <span className="font-medium">{(job.evaluationCriteria.technical * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Experiência:</span>
                    <span className="font-medium">{(job.evaluationCriteria.experience * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cultura:</span>
                    <span className="font-medium">{(job.evaluationCriteria.culture * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Comunicação:</span>
                    <span className="font-medium">{(job.evaluationCriteria.communication * 100).toFixed(0)}%</span>
                  </div>
                </>
              ) : (
                <ul className="list-disc list-inside space-y-2">
                  {Array.isArray(job.evaluationCriteria) && job.evaluationCriteria.map((criteria: any, index: number) => (
                    <li key={index} className="text-muted-foreground">
                      {criteria.name || criteria}: {criteria.weight ? `${(criteria.weight * 100).toFixed(0)}%` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
